import { FC, useEffect, useState } from "react";
import GridWrapper from "../../../../../components/controls/GridWrapper";
import { NumberInput, TextAreaInput, TextInput } from "../../../../../components/controls/inputs";
import { BaseModalWindow } from "../../../../../components/modalWindows/BaseModalWindow";
import modalStyles from "../../../../../components/modalWindows/styles/BaseModalWindow.module.scss";
import { ILotDTO } from "../../../../../libs/coreapi-dto/accounting/lot";
import { IInternetOrderItemCreateDTO } from "../../../../../libs/coreapi-dto/accounting/internetOrderItem";
import { v4 as uuidv4 } from "uuid";
import { ICreateItem } from "../InternetOrderUpdateForm";
import { KizBoxEntity } from "../../../../Dictionaries/Kiz/KizBoxEntity";
import { IKizDTO } from "../../../../../libs/coreapi-dto/dirs/kiz";
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { KizDataProvider } from "../../../../../Services/DataProviders/KizDataProvider";
import buffer from "buffer";
import { useLotRemain } from "../../../../../components/lotSelector/useLotRemain";
import useOperativeReserve from "../../../../../components/lotSelector/useOperativeReserve";
import { IdTableVariant } from "../../../../../@types/enumsGlobal";
import { useForm } from "../../../../../system/hooks/useForm";
import { DateTime } from "luxon";
import { GoodsDataProvider } from "../../../../../Services/DataProviders/GoodsDataProvider";
import { roundMoneyFormat } from "../../../../../system/functions/round";

interface IInternetOrderCreateItemModal {
    idDocumentGlobal: string;
    idDocumentItemGlobal: string;
    lot: ILotDTO;
    ok: (createItem: ICreateItem) => void;
    scanData?: string;
    cancel: () => void;
    items: IInternetOrderItemCreateDTO[];
}

interface IValidator {
    quantity: number;
}

export const InternetOrderCreatorItemModal: FC<IInternetOrderCreateItemModal> = (props) => {
    const appCtx = useAppContext();
    const kizDataProvider = new KizDataProvider(appCtx.coreApiService);
    const goodsDP = new GoodsDataProvider(appCtx.coreApiService);

    const [createItem, setCreateItem] = useState<IInternetOrderItemCreateDTO>({} as IInternetOrderItemCreateDTO);
    const [idGlobal, setIdGlobal] = useState<string>(props.idDocumentItemGlobal ?? uuidv4());
    const [isGnvls, setIsGnvls] = useState<boolean>(false);

    useEffect(() => {
        if (props.scanData) {
            const scanDataBase64: string = buffer.Buffer.from(props.scanData, "ascii").toString("base64");
            if (KizBoxEntity.isKizBox(props.scanData)) {
                kizDataProvider.parse(scanDataBase64, (parsedBarcode) => {
                    setCreateItem({
                        idGlobal: idGlobal,
                        quantity: 0,
                        supplierCostInfo: props.lot.supplierCostInfo,
                        retailCostInfo: props.lot.retailCostInfo,
                        isKiz: props.lot.isKiz,
                        kizs: [] as IKizDTO[],
                        dateCreated: DateTime.now()
                    } as IInternetOrderItemCreateDTO);
                });
            } else {
                kizDataProvider.parse(scanDataBase64, (parsedBarcode) => {
                    setCreateItem({
                        idGlobal: idGlobal,
                        quantity: 0,
                        supplierCostInfo: props.lot.supplierCostInfo,
                        retailCostInfo: props.lot.retailCostInfo,
                        isKiz: props.lot.isKiz,
                        kizs: [{ ...parsedBarcode.kizData, idGlobal: uuidv4() }] as IKizDTO[],
                        dateCreated: DateTime.now()
                    } as IInternetOrderItemCreateDTO);
                });
            }
        } else {
            setCreateItem({
                idGlobal: idGlobal,
                supplierCostInfo: props.lot.supplierCostInfo,
                retailCostInfo: props.lot.retailCostInfo,
                quantity: 1,
                isKiz: props.lot.isKiz,
                kizs: [] as IKizDTO[],
                dateCreated: DateTime.now()
            } as IInternetOrderItemCreateDTO);
        }
    }, []);
    
    useEffect(() => {
        if (props.lot.goods) {
            goodsDP.getById(props.lot.goods.idGlobal, (e) => {
                setIsGnvls(e.gnvls.important ?? false);
            });
        }
    }, []);

    const buildDocumentItem = (item: IInternetOrderItemCreateDTO | undefined): ICreateItem => {
        let result = item ?? createItem;
        return {
            item: {
                ...result,
                idLotExternalGlobal: props.lot.idGlobal,
                goodsName: props.lot.goods.displayName,
                supplierCostInfo: {
                    ...props.lot.supplierCostInfo,
                    vat: props.lot.supplierCostInfo.vat,
                    sum: roundMoneyFormat(props.lot.supplierCostInfo.price * result.quantity),
                    vatSum: roundMoneyFormat(props.lot.supplierCostInfo.vatPrice * result.quantity),
                    sumIncVat: roundMoneyFormat(props.lot.supplierCostInfo.priceIncVat * result.quantity),
                },
                retailCostInfo: {
                    ...props.lot.retailCostInfo,
                    vat: props.lot.retailCostInfo.vat,
                    sum: roundMoneyFormat(props.lot.retailCostInfo.price * result.quantity),
                    vatSum: roundMoneyFormat(props.lot.retailCostInfo.vatPrice * result.quantity),
                    sumIncVat: roundMoneyFormat(props.lot.retailCostInfo.priceIncVat * result.quantity),
                },
                
                producerName: props.lot.producer.displayName,
                isGnvls: isGnvls,
                kizCount: result.kizs?.length ?? 0,
                isKiz: props.lot.isKiz,
                dateCreated: DateTime.now()
            },
            displayItem: {
                idGlobal: result.idGlobal,
                idLotExternalGlobal: props.lot.idGlobal!,
                goodsName: result.goodsName,
                lotName: props.lot.displayName,
                scalingRatioDisplayName: props.lot.scalingRatio.displayName,
                quantity: result.quantity,
                price: result.price,
                discount: props.lot.retailCostInfo.priceIncVat - result.price,

                supplierVat: props.lot.supplierCostInfo.vat,
                supplierPrice: props.lot.supplierCostInfo.price,
                supplierVatPrice: props.lot.supplierCostInfo.vatPrice,
                supplierPriceIncVat: props.lot.supplierCostInfo.priceIncVat,
                supplierSum: roundMoneyFormat(props.lot.supplierCostInfo.price * result.quantity),
                supplierVatSum: roundMoneyFormat(props.lot.supplierCostInfo.vatPrice * result.quantity),
                supplierSumIncVat: roundMoneyFormat(props.lot.supplierCostInfo.priceIncVat * result.quantity),
                supplierAdprice: props.lot.supplierCostInfo.adprice,

                retailVat: props.lot.retailCostInfo.vat,
                retailPrice: props.lot.retailCostInfo.price,
                retailVatPrice: props.lot.retailCostInfo.vatPrice,
                retailPriceIncVat: props.lot.retailCostInfo.priceIncVat,
                retailSum: roundMoneyFormat(props.lot.retailCostInfo.price * result.quantity),
                retailVatSum: roundMoneyFormat(props.lot.retailCostInfo.vatPrice * result.quantity),
                retailSumIncVat: roundMoneyFormat(props.lot.retailCostInfo.priceIncVat * result.quantity),
                retailAdprice: props.lot.retailCostInfo.adprice,
                
                producer: props.lot.producer.displayName,
                isGnvls: isGnvls,
                kizCount: result.kizs?.length ?? 0,
                isKiz: props.lot.isKiz ?? false,
                dateCreated: DateTime.now()
            },
            filter: {
                idItem: result.idGlobal,
                quantityMax: props.lot.quantityRem + props.lot.quantityRes,
                quantityMin: 0,
            },
        };
    };

    const remain = useLotRemain(props.lot.idGlobal, props.idDocumentGlobal);
    const operativeReserve = useOperativeReserve();

    const onOk = () => {
        let id = idGlobal;
        let quantity = createItem.quantity;
        let newCreateItem;

        let item = props.items.find((c) => c.idLotExternalGlobal && c.idLotExternalGlobal === createItem.idLotExternalGlobal);
        if (item) {
            id = item.idGlobal;
            quantity = item.quantity + quantity;

            newCreateItem = {
                ...item,
                quantity: quantity,
            } as IInternetOrderItemCreateDTO;
        }

        operativeReserve.send(
            {
                idLot: props.lot.idGlobal,
                idTable: IdTableVariant.InternetOrder,
                idDocument: props.idDocumentGlobal,
                idDocumentItem: id,
                quantity: quantity,
            },
            () => props.ok(buildDocumentItem(newCreateItem))
        );
    };

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            quantity: {
                required: {
                    value: createItem.quantity && createItem.quantity > 0 ? false : true,
                    message: "Значение должно быть больше 0",
                },
            },
        },
    });

    return (
        <BaseModalWindow
            header="Создание позиции"
            ok={{
                onClick: () => {
                    if (isValid()) {
                        onOk();
                    }
                },
            }}
            cancel={{ onClick: props.cancel }}
            modalWindowClassName={modalStyles.modalWindowInternetOrder}
            footerStyle="confirm"
        >
        <TextAreaInput label="Товар" value={props.lot.goods.displayName} disabled />
        <TextInput label="Партия" disabled value={props.lot.docNum} />
        <GridWrapper cols={3}>
            <TextInput label="Единица измерения" value={props.lot.scalingRatio.displayName} disabled />
            <NumberInput label="Остаток" value={remain} disabled />
            <NumberInput
                required
                onFocus={() => setErrors({ ...errors, quantity: undefined })}
                error={errors.quantity}
                keyBan={true}
                label="Количество"
                value={createItem.quantity}
                min={1}
                max={remain}
                onChange={(value) => setCreateItem({ ...createItem, quantity: value })}
            />
            </GridWrapper>
    </BaseModalWindow>
    );
};